import { ModalCloseButton } from "../Components/LucidePrimitives";
import { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Spinner,
  Switch,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { Database } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useAppToast } from "../Components/AppToast.jsx";
import { useAnimationToggle } from "../Contexts/AnimationContext.jsx";
import PropTypes from "prop-types";
import { useLocation, useHistory, withRouter } from "react-router-dom";
import Backend from "../utils/api.js";
import Validators from "../utils/validators.jsx";
import ProfileAppBar from "./Components/ProfileAppBar.jsx";
import OAuthFailureAlert from "./Components/OAuthFailureAlert.jsx";
import { getGravatarUrl } from "../utils/gravatar";
import AppFooter from "../Components/AppFooter.jsx";
import useVersion from "../hooks/useVersion.jsx";
import { markOAuthStarted, takeAbandonedAttempt } from "../utils/badgeOAuthTracking";

// Debug-only: dumps the raw QR token string under the code so it can be
// read/copied without a scanner (e.g. to type it into a kiosk by hand while
// testing). CRA only exposes env vars prefixed REACT_APP_ to the bundle, so
// the flag is REACT_APP_LOCAL_DEBUG; unset (the default in every non-local
// build) this is a no-op and nothing extra renders.
const LOCAL_DEBUG = process.env.REACT_APP_LOCAL_DEBUG === "true";

/*
 * This component provides a full view of the authenticated user's "profile."
 * Provides a way to edit user information and connect badge provider accounts.
 * Additionally provides an entry point to the Admin Dashboard for admins.
 */
function Profile({ user }) {
  const { animationsEnabled, setAnimationsEnabled } = useAnimationToggle();
  const toast = useAppToast();
  const location = useLocation();
  const routerHistory = useHistory();
  const [userState, setUserState] = useState(user);
  const [badgeProviders, setBadgeProviders] = useState([]);
  const [providerStatus, setProviderStatus] = useState({});
  const [v1Linked, setV1Linked] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isQrModalOpen, onOpen: onQrModalOpen, onClose: onQrModalClose } = useDisclosure();
  const [earnedBadges, setEarnedBadges] = useState(null);
  const [badgesFromCache, setBadgesFromCache] = useState(false);
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [badgesError, setBadgesError] = useState(null);
  const [isSwipedIn, setIsSwipedIn] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [qrToken, setQrToken] = useState(null);
  const [qrExpiresAt, setQrExpiresAt] = useState(null);
  const [qrSecondsLeft, setQrSecondsLeft] = useState(0);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState(null);
  const qrTokenRef = useRef(null);
  // Set when the user dismisses the QR modal (or leaves the page) while a
  // generate request is still in flight. The mint has already happened
  // server-side by the time that response lands, so rather than surfacing
  // a token into state with no UI left showing it, the success handler
  // below cancels it immediately instead.
  const qrDismissedWhileLoadingRef = useRef(false);

  // Set from the OAuth callback on failure; shown as a persistent alert on
  // the provider card so the user gets next steps, not just a toast.
  const [oauthFailure, setOauthFailure] = useState(null);

  useEffect(() => {
    if (location.state?.badgrResult) {
      const { badgrResult: result, badgrError, badgrMessage, badgrProvider } = location.state;
      const providerName = location.state.providerDisplayName || "Parchment Digital Badges";
      if (result === "success") {
        toast({ title: `${providerName} connected successfully.`, status: "success" });
      } else if (result === "cancelled") {
        toast({ title: `${providerName} connection cancelled.`, status: "info" });
      } else {
        toast({ title: `Couldn't connect ${providerName}.`, status: "error" });
        setOauthFailure({ error: badgrError, message: badgrMessage, provider: badgrProvider });
      }
      routerHistory.replace({ pathname: "/profile", state: {} });
    } else {
      // Left for the provider but the callback never ran (see
      // utils/badgeOAuthTracking.js). Tell them a retry should go through.
      const abandoned = takeAbandonedAttempt();
      if (abandoned) {
        setOauthFailure({ error: "not_returned", provider: abandoned.provider });
      }
    }
  }, []);

  const { version, environment: versionEnv } = useVersion();

  useEffect(() => {
    const callbackUrl = window.location.origin + "/oauth/callback";
    Backend.listBadgeProviders(callbackUrl)
      .then((res) => setBadgeProviders(res.data || []))
      .catch(() => {});
    Backend.checkBadgrAuthentication(userState.UID)
      .then((res) => {
        setProviderStatus(res.providers || {});
        setV1Linked(res.v1Linked || false);
      })
      .catch(() => {});
    Backend.isUserSwipedInAnywhere(userState.UID)
      .then(setIsSwipedIn)
      .catch(() => {});
  }, []);

  // Kept in sync with qrToken so the unmount cleanup below can see the
  // latest value without re-registering its effect on every generate/cancel.
  useEffect(() => {
    qrTokenRef.current = qrToken;
  }, [qrToken]);

  // Ticks the countdown shown under the QR code and clears it client-side
  // once the 5 minute TTL runs out (the backend token is already dead by
  // then — Redis enforces that independently).
  useEffect(() => {
    if (!qrExpiresAt) return;
    const tick = () => {
      const secondsLeft = Math.max(0, Math.round(qrExpiresAt - Date.now() / 1000));
      setQrSecondsLeft(secondsLeft);
      if (secondsLeft <= 0) {
        setQrToken(null);
        setQrExpiresAt(null);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [qrExpiresAt]);

  // Leaving the profile page with a live QR code still on screen would
  // leave a working login credential nobody can see to scan. Cancel it
  // server-side on unmount rather than just letting the TTL run out.
  // Also covers navigating away while a generate request is still in
  // flight — see qrDismissedWhileLoadingRef in handleGenerateQr.
  useEffect(() => {
    return () => {
      qrDismissedWhileLoadingRef.current = true;
      if (qrTokenRef.current) {
        Backend.cancelQrLoginToken(userState.UID).catch(() => {});
      }
    };
  }, [userState.UID]);

  function formatCountdown(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  function handleGenerateQr() {
    qrDismissedWhileLoadingRef.current = false;
    setQrLoading(true);
    setQrError(null);
    onQrModalOpen();
    Backend.generateQrLoginToken(userState.UID)
      .then((res) => {
        if (res.status === 201) {
          if (qrDismissedWhileLoadingRef.current) {
            // Modal was closed (or the page was left) before this
            // resolved — the token already exists server-side, so cancel
            // it rather than leaving it live with nothing on screen to
            // show or cancel it.
            Backend.cancelQrLoginToken(userState.UID).catch(() => {});
            return;
          }
          setQrToken(res.token);
          setQrExpiresAt(res.expiresAt);
        } else if (res.status === 503) {
          setQrError("QR check-in is temporarily unavailable. Please try again shortly.");
        } else {
          setQrError("Couldn't generate a QR code. Please try again.");
        }
      })
      .catch(() => setQrError("Couldn't generate a QR code. Please try again."))
      .finally(() => setQrLoading(false));
  }

  function handleCancelQr() {
    const hadToken = !!qrToken;
    if (qrLoading) {
      qrDismissedWhileLoadingRef.current = true;
    }
    setQrToken(null);
    setQrExpiresAt(null);
    setQrError(null);
    onQrModalClose();
    if (hadToken) {
      Backend.cancelQrLoginToken(userState.UID).catch(() => {});
    }
  }

  function handleSignOut() {
    setSigningOut(true);
    Backend.selfSwipeOut(userState.UID)
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          setIsSwipedIn(false);
          toast({ title: "Checked out of your space session", status: "success" });
        } else {
          toast({ title: "Failed to check out", status: "error" });
        }
      })
      .catch(() => toast({ title: "Failed to check out", status: "error" }))
      .finally(() => setSigningOut(false));
  }

  function handleInfoClick() {
    onOpen();
    if (earnedBadges !== null) return;
    setBadgesLoading(true);
    setBadgesError(null);
    Backend.getUserEarnedBadges(userState.UID)
      .then((res) => {
        if (res.status >= 400) {
          setBadgesError(res.error === 'rate_limited' ? 'rate_limited' : 'generic');
          return;
        }
        setEarnedBadges(res.data || []);
        setBadgesFromCache(!!res.from_cache);
      })
      .catch(() => setBadgesError('generic'))
      .finally(() => setBadgesLoading(false));
  }

  return (
    <Flex direction="column" minH="100vh" bg="gray.50">
      <ProfileAppBar user={user} signoutLocation="/logout" maxW="container.lg" />
      <Box
        px={{ base: 4, md: 8 }}
        py={8}
        maxW="container.lg"
        mx="auto"
        w="full"
      >
        <Flex align="center" mb={8} gap={5}>
          <Box position="relative" flexShrink={0}>
            <Link
              href="https://gravatar.com/profile"
              isExternal
              rounded="full"
              display="block"
              role="group"
              _focus={{ boxShadow: "outline" }}
            >
              <Avatar
                size="xl"
                name={`${userState.firstName} ${userState.lastName}`}
                src={getGravatarUrl(userState.email, 160)}
                bg="brand.red"
                color="white"
                getInitials={(name) => {
                  const parts = name.split(" ").filter(Boolean);
                  return parts.map((p) => p[0]).join("").toUpperCase();
                }}
                cursor="pointer"
              />
              <Flex
                position="absolute"
                inset={0}
                rounded="full"
                bg="blackAlpha.500"
                align="center"
                justify="center"
                direction="column"
                opacity={0}
                transition="opacity 0.15s"
                _groupHover={{ opacity: 1 }}
              >
                <Icon viewBox="0 0 24 24" boxSize={5} color="white" mb={0.5}>
                  <path
                    fill="currentColor"
                    d="M12 15a3 3 0 100-6 3 3 0 000 6z"
                  />
                  <path
                    fill="currentColor"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.344 2.356a1 1 0 01.866-.5h3.58a1 1 0 01.866.5L15.87 4.5H19a3 3 0 013 3v10a3 3 0 01-3 3H5a3 3 0 01-3-3v-10a3 3 0 013-3h3.13l1.214-2.144zM12 8a4 4 0 100 8 4 4 0 000-8z"
                  />
                </Icon>
                <Text fontSize="10px" color="white" fontWeight={600} lineHeight="1">
                  Edit
                </Text>
              </Flex>
            </Link>
            <Popover placement="bottom-start" trigger="hover">
              <PopoverTrigger>
                <Flex
                  as="button"
                  position="absolute"
                  bottom={0}
                  right={0}
                  w={6}
                  h={6}
                  rounded="full"
                  bg="white"
                  shadow="sm"
                  borderWidth="1px"
                  borderColor="gray.200"
                  align="center"
                  justify="center"
                  cursor="pointer"
                  transition="border-color 0.15s, shadow 0.15s"
                  _hover={{ borderColor: "gray.300", shadow: "md" }}
                  zIndex={1}
                  aria-label="Learn about profile photos"
                >
                  <Icon viewBox="0 0 16 16" boxSize={3} color="gray.500">
                    <path
                      fill="currentColor"
                      d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 2.5a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5zM9 11H7V7h2v4z"
                    />
                  </Icon>
                </Flex>
              </PopoverTrigger>
              <PopoverContent
                w="280px"
                rounded="lg"
                shadow="lg"
                borderColor="gray.100"
                _focus={{ outline: "none" }}
              >
                <PopoverBody p={4}>
                  <Text fontSize="sm" color="gray.700" mb={2}>
                    Profile photos are powered by{" "}
                    <Link href="https://gravatar.com" isExternal fontWeight={600} color="gray.800">
                      Gravatar
                    </Link>
                    , a free service that links a photo to your email address.
                  </Text>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    Create an account with your Pinpoint email and your photo
                    will appear here automatically. Click your avatar to get started.
                  </Text>
                  <Link
                    href="https://docs.pinpoint.umd.edu/docs/guides/users/profile-photo"
                    isExternal
                    fontSize="sm"
                    fontWeight={600}
                    color="gray.500"
                    _hover={{ color: "gray.700" }}
                  >
                    Learn more
                  </Link>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Box>
          <Box>
            <Heading as="h1" size="xl" color="gray.800">
              Hey {userState.firstName} 👋
            </Heading>
            <Text color="gray.600" mt={1}>
              Update your profile or connect your Parchment Digital Badges account.
            </Text>
          </Box>
        </Flex>

        <Flex
          direction={{ base: "column", md: "row" }}
          gap={8}
        >
          <Flex direction="column" flex={1} gap={8}>
            {badgeProviders.map((p) => {
              const status = providerStatus[p.name] || { linked: false, identity_count: 0 };
              return (
                <Box key={p.name} bg="white" rounded="lg" shadow="md" p={6}>
                  <Flex align="center" justify="space-between" mb={4}>
                    <Heading as="h2" size="md" color="gray.800">
                      {p.display_name}
                    </Heading>
                    <Flex align="center" gap={2}>
                      {status.linked && (
                        <>
                          <Badge colorScheme="blue" variant="subtle">V2</Badge>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={handleInfoClick}
                            aria-label="View recognized badges"
                            px={1}
                            minW="auto"
                          >
                            <Icon viewBox="0 0 16 16" boxSize={3.5} color="gray.500">
                              <path fill="currentColor" d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 2.5a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5zM9 11H7V7h2v4z" />
                            </Icon>
                          </Button>
                        </>
                      )}
                      {!status.linked && v1Linked && (
                        <Badge colorScheme="orange" variant="subtle">Expired</Badge>
                      )}
                    </Flex>
                  </Flex>
                  {/* Only on the card of the provider that failed. */}
                  {oauthFailure && oauthFailure.provider === p.name && (
                    <OAuthFailureAlert
                      failure={oauthFailure}
                      provider={p}
                    />
                  )}
                  <Text color="gray.600">
                    {status.linked
                      ? `Connected (${status.identity_count} email${status.identity_count !== 1 ? "s" : ""} linked). Reconnect if you're experiencing issues.`
                      : v1Linked
                      ? `Your ${p.display_name} connection has expired and no longer grants access to badge-gated tools. Please reconnect to restore access.`
                      : `Connect your ${p.display_name} account to access spaces that require digital badges.`}
                  </Text>
                  <Button
                    variant={status.linked ? "outline" : "brand"}
                    w="full"
                    mt={6}
                    fontSize={{ base: "sm", sm: "md" }}
                    whiteSpace="normal"
                    wordBreak="break-word"
                    onClick={() => {
                      markOAuthStarted(p.name, p.oauth_url);
                      window.location.assign(p.oauth_url);
                    }}
                  >
                    {status.linked || v1Linked
                      ? `Reconnect to ${p.display_name}`
                      : `Connect to ${p.display_name}`}
                  </Button>
                </Box>
              );
            })}

            <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
              <ModalOverlay />
              <ModalContent rounded="lg">
                <ModalHeader fontSize="md">
                  <HStack spacing={2}>
                    <Text>Your Recognized Badges</Text>
                    {badgesFromCache && (
                      <Tooltip label="Badge data served from cache" placement="top">
                        <Box color="gray.400" display="inline-flex" alignItems="center">
                          <Database size={14} aria-hidden="true" />
                        </Box>
                      </Tooltip>
                    )}
                  </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                  {badgesLoading && (
                    <Flex justify="center" py={6}>
                      <Spinner />
                    </Flex>
                  )}
                  {badgesError && (
                    <Text color="red.500" fontSize="sm">
                      {badgesError === 'rate_limited'
                        ? 'Parchment is temporarily busy. Please wait a moment and try again.'
                        : 'Could not load badges. Please try again.'}
                    </Text>
                  )}
                  {!badgesLoading && !badgesError && earnedBadges?.length === 0 && (
                    <Text color="gray.500" fontSize="sm">
                      No recognized badges found. Earn badges through Parchment to unlock equipment access.
                    </Text>
                  )}
                  {!badgesLoading && !badgesError && earnedBadges?.length > 0 && (
                    <VStack align="stretch" spacing={0}>
                      {earnedBadges.map((b) => (
                        <Box
                          key={b.id}
                          py={2}
                          borderBottomWidth="1px"
                          borderColor="gray.100"
                          _last={{ borderBottom: "none" }}
                        >
                          <Text fontWeight="500" fontSize="sm">{b.name}</Text>
                          {b.is_trainer && (
                            <Badge colorScheme="purple" variant="subtle" mt={1} fontSize="xs">
                              Trainer
                            </Badge>
                          )}
                        </Box>
                      ))}
                    </VStack>
                  )}
                </ModalBody>
              </ModalContent>
            </Modal>

            <Box bg="white" rounded="lg" shadow="md" p={6}>
              <Flex align="center" gap={2} mb={4}>
                <Icon viewBox="0 0 24 24" boxSize={6} color="brand.red">
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z"
                  />
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z"
                  />
                </Icon>
                <Heading as="h2" size="md" color="gray.800">
                  QR check-in
                </Heading>
              </Flex>
              <Text color="gray.600" mb={6}>
                Generate a QR code to check in at a space kiosk without your ID
                card. Codes expire after 5 minutes and can only be scanned once.
              </Text>
              <Button
                variant="brand"
                w="full"
                isLoading={qrLoading}
                onClick={handleGenerateQr}
              >
                Check in with QR code
              </Button>
            </Box>

            <Box bg="white" rounded="lg" shadow="md" p={6}>
              <Heading as="h2" size="md" mb={4} color="gray.800">
                Preferences
              </Heading>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel htmlFor="animation-toggle" mb={0} fontWeight="500">
                    Animations
                  </FormLabel>
                  <Text fontSize="sm" color="gray.500">
                    Toggle animations and transitions across the app.
                  </Text>
                </Box>
                <Switch
                  id="animation-toggle"
                  isChecked={animationsEnabled}
                  sx={{
                    "span.chakra-switch__track[data-checked]": {
                      bg: "brand.red",
                    },
                  }}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setAnimationsEnabled(enabled);
                    toast({
                      title: `Animations ${enabled ? "enabled" : "disabled"}`,
                      status: "success",
                      duration: 2000,
                      isClosable: true,
                    });
                  }}
                  aria-label={animationsEnabled ? "Disable animations" : "Enable animations"}
                />
              </FormControl>
            </Box>

            <Box bg="white" rounded="lg" shadow="md" p={6}>
              <Heading as="h2" size="md" mb={4} color="gray.800">
                Space session
              </Heading>
              <Text color="gray.600" mb={6}>
                {isSwipedIn
                  ? "You're currently checked into a space. Check out below if you're done for now."
                  : "You're not currently checked into a space."}
              </Text>
              <Button
                colorScheme="red"
                w="full"
                isDisabled={!isSwipedIn}
                isLoading={signingOut}
                onClick={handleSignOut}
              >
                Check out
              </Button>
            </Box>
          </Flex>

          <Box
            flex={1}
            bg="white"
            rounded="lg"
            shadow="md"
            p={6}
            alignSelf="flex-start"
          >
            <Heading as="h2" size="md" mb={4} color="gray.800">
              Edit Profile
            </Heading>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!Validators.validateUser(userState)) {
                  toast({
                    title: "Please fix the highlighted fields before saving.",
                    status: "error",
                    duration: 4000,
                  });
                  return;
                }
                // Send only fields this page lets you edit. Email, swipe
                // number, directory ID and admin status are admin-only.
                const edits = { firstName: userState.firstName, lastName: userState.lastName };
                if (userState.isSuperAdmin) edits.email = userState.email;
                Backend.editUser({ UID: userState.UID, args: edits })
                  .then((res) => {
                    if (res.status === 200 || res.status === 201) {
                      return Backend.getUser(userState.UID).then((freshUser) => {
                        if (freshUser.status === 200) {
                          setUserState(freshUser);
                        }
                        toast({ title: "Profile changes saved", status: "success" });
                      });
                    } else {
                      toast({
                        title: "Failed to save changes",
                        description: res.message,
                        status: "error",
                      });
                    }
                  })
                  .catch(() => {
                    toast({ title: "Failed to save changes", status: "error" });
                  });
              }}
            >
              <Flex gap={4}>
                <FormControl id="firstName">
                  <FormLabel>First Name</FormLabel>
                  <Input
                    type="text"
                    placeholder="First name"
                    value={userState.firstName}
                    onChange={(e) => {
                      Validators.constrainNameInput(e.target.value) &&
                        setUserState({ ...userState, firstName: e.target.value });
                    }}
                    isInvalid={!Validators.validateName(userState.firstName)}
                  />
                </FormControl>
                <FormControl id="lastName">
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    type="text"
                    placeholder="Last name"
                    value={userState.lastName}
                    onChange={(e) => {
                      Validators.constrainNameInput(e.target.value) &&
                        setUserState({ ...userState, lastName: e.target.value });
                    }}
                    isInvalid={!Validators.validateName(userState.lastName)}
                  />
                </FormControl>
              </Flex>
              <FormControl id="email" mt={4}>
                <FormLabel>Email</FormLabel>
                {!userState.isSuperAdmin && (
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Contact space staff to change your email.
                  </Text>
                )}
                <Input
                  type="email"
                  placeholder="Email"
                  value={userState.email}
                  disabled={!userState.isSuperAdmin}
                  onChange={(e) => {
                    setUserState({ ...userState, email: e.target.value });
                  }}
                  isInvalid={!Validators.validateEmail(userState.email)}
                />
              </FormControl>
              <FormControl id="uid" mt={4}>
                <FormLabel>University ID</FormLabel>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  This field cannot be edited.
                </Text>
                <Input
                  type="text"
                  placeholder="111222333"
                  value={userState.UID}
                  disabled={true}
                />
              </FormControl>
              <FormControl id="directoryId" mt={4}>
                <FormLabel>Directory ID</FormLabel>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  This field cannot be edited.
                </Text>
                <Input
                  type="text"
                  placeholder="Directory ID"
                  value={userState.directoryID}
                  maxLength={9}
                  disabled={true}
                  onChange={(e) => {
                    setUserState({ ...userState, directoryID: e.target.value });
                  }}
                  isInvalid={
                    !Validators.validateDirectoryID(userState.directoryID)
                  }
                />
              </FormControl>
              <Button type="submit" variant="brand" w="full" mt={6}>
                Save Changes
              </Button>
            </form>
          </Box>
        </Flex>
      </Box>

      <Modal isOpen={isQrModalOpen} onClose={handleCancelQr} isCentered size="sm">
        <ModalOverlay />
        <ModalContent rounded="lg">
          <ModalHeader fontSize="md">QR check-in</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {qrError && (
              <Text color="red.500" fontSize="sm" mb={4}>
                {qrError}
              </Text>
            )}
            {qrToken ? (
              <VStack spacing={4}>
                <Text
                  fontSize="sm"
                  color="red.700"
                  bg="red.50"
                  border="1px solid"
                  borderColor="red.200"
                  rounded="md"
                  px={3}
                  py={2}
                  textAlign="center"
                >
                  Tip: turn your screen brightness all the way up so the
                  kiosk reader can scan the code.
                </Text>
                <Box p={4} bg="white" borderWidth="1px" borderColor="gray.200" rounded="md">
                  <QRCodeSVG value={qrToken} size={200} />
                </Box>
                {LOCAL_DEBUG && (
                  <Text
                    fontSize="xs"
                    fontFamily="monospace"
                    color="gray.400"
                    textAlign="center"
                    wordBreak="break-all"
                    userSelect="all"
                  >
                    {qrToken}
                  </Text>
                )}
                <Text fontWeight="600" color={qrSecondsLeft <= 30 ? "red.500" : "gray.700"}>
                  Expires in {formatCountdown(qrSecondsLeft)}
                </Text>
                <Button colorScheme="red" variant="outline" w="full" onClick={handleCancelQr}>
                  Cancel Code
                </Button>
              </VStack>
            ) : (
              !qrError && (
                <Flex justify="center" py={6}>
                  <Spinner />
                </Flex>
              )
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <AppFooter version={version} environment={versionEnv} />
    </Flex>
  );
}

Profile.propTypes = {
  user: PropTypes.shape({
    UID: PropTypes.number.isRequired,
  }).isRequired,
};

export default withRouter(Profile);
